import { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ImageAnalysisResults } from '@/components/ui/ImageAnalysisResults'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { getAnalysisRun } from '@/api/avpulse-api'
import { ProcessingProgress } from '@/components/ProcessingProgress'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface ImageAnalysisData {
  runId: string
  runName?: string
  predictionUrl: string
  valence: number
  arousal: number
  explanation?: string
  error?: string
}

const CompareImageAnalyses = () => {
  const [searchParams] = useSearchParams()

  // Memoize the runIds array to prevent unnecessary re-renders
  const runIds = useMemo(() => {
    const runIdsParam = searchParams.get('runIds')
    return runIdsParam ? runIdsParam.split(',').filter(Boolean) : []
  }, [searchParams])

  const [analyses, setAnalyses] = useState<ImageAnalysisData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const setApi = (api: any) => {
    if (!api) return
    // Initialize current slide
    setCurrentSlide(api.selectedScrollSnap())
    // Update on selection change
    api.on('select', () => setCurrentSlide(api.selectedScrollSnap()))
  }

  // Fetch analysis data for all run IDs
  useEffect(() => {
    const fetchAnalyses = async () => {
      console.log('fetchAnalyses called with runIds:', runIds) // Debug log

      if (runIds.length === 0) {
        setError('No run IDs provided for comparison')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const analysesData: ImageAnalysisData[] = []

        for (const runId of runIds) {
          try {
            const runResult = await getAnalysisRun(runId)
            const prediction = runResult.predictions.predictions

            analysesData.push({
              runId,
              runName: runResult.run.runName,
              predictionUrl: prediction.prediction_url,
              valence: prediction.emotion_valence,
              arousal: prediction.emotion_arousal,
              explanation: prediction.explanation,
            })
          } catch (err: any) {
            console.error(`Failed to fetch run ${runId}:`, err)
            analysesData.push({
              runId,
              runName: `Analysis ${runId.slice(-8)}`,
              predictionUrl: '',
              valence: 0,
              arousal: 0,
              error: err.message || 'Failed to load analysis',
              explanation: '',
            })
          }
        }

        setAnalyses(analysesData)
      } catch (err: any) {
        setError(err.message || 'Failed to load analyses')
        toast.error('Failed to load analyses')
      } finally {
        setLoading(false)
      }
    }

    fetchAnalyses()
  }, [runIds]) // Only depend on runIds

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <ProcessingProgress message="Loading analyses for comparison..." />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (analyses.length === 0) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Analyses</AlertTitle>
          <AlertDescription>No analyses found for comparison.</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <CardHeader className="text-center pb-8">
        <CardTitle className="text-3xl font-bold">
          Analyses Comparison
        </CardTitle>
      </CardHeader>

      {/* Carousel */}
      <div className="max-w-4xl mx-auto">
        <Carousel
          opts={{
            align: 'start',
            loop: false,
          }}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent>
            {analyses.map(analysis => (
              <CarouselItem key={analysis.runId}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">
                      {analysis.runName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analysis.error ? (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{analysis.error}</AlertDescription>
                      </Alert>
                    ) : (
                      <ImageAnalysisResults
                        predictionUrl={analysis.predictionUrl}
                        valence={analysis.valence}
                        arousal={analysis.arousal}
                        explanation={analysis.explanation}
                        size="medium"
                        showChart={true}
                        showExplanation={true}
                      />
                    )}
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
        <div className="text-foreground py-2 text-center text-sm">
          Analysis {currentSlide + 1} of {analyses.length}
        </div>
      </div>
    </div>
  )
}

export default CompareImageAnalyses
